'use client'

import useQueryParams from '@/app/providers/useQueryParams'
import { useLocale } from '@/components/Link'
import type { UserBasketSettings } from '@/components/page-specific/catering/common/domain/user-basket-settings'
import { lookupAddressDetails } from '@/components/page-specific/catering/common/helpers/addresses'
import { getQueryDateTime } from '@/components/page-specific/catering/common/helpers/date'
import type { AppErrors } from '@/components/page-specific/catering/common/types/AppErrors'
import type { UserBasketSettingsLocationFragment } from '@/generated/graphql'
import { useSsrFriendlyLocalStorage } from '@/hooks/useSsrFriendlyLocalStorage'
import { useApolloClient } from '@apollo/client'

import { addMinutes } from 'date-fns/addMinutes'
import { format } from 'date-fns/format'
import _ from 'lodash'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

const defaultUserState: Partial<UserBasketSettings> = {}

export const useUserState = () => {
  const [errors, setErrors] = useState<AppErrors>({})
  const pathname = usePathname()
  const locale = useLocale()
  const apolloClient = useApolloClient()

  const [userState, setUserState] = useSsrFriendlyLocalStorage<UserBasketSettings>(
    'userBasketSettings',
    defaultUserState,
  )

  const updateErrors = (newErrors: AppErrors) => {
    // Filter out null values before updating errors.
    const newErrorsCopy = _.omitBy(newErrors, _.isNull)
    setErrors({ ...newErrorsCopy })
  }

  const updateUserState = useCallback(
    (newUserState: Partial<UserBasketSettings>) => {
      setUserState((prev) => ({ ...defaultUserState, ...prev, ...newUserState }))
    },
    [setUserState],
  )

  const { postcode, street, date, time } = useQueryParams()

  useEffect(() => {
    if (!pathname?.includes('/office-catering/vendors')) return

    const collectDetails = async () => {
      let newLocation: UserBasketSettingsLocationFragment | null = null

      if (postcode && street) {
        const foundAddress = await lookupAddressDetails(
          { street, postcode, city: '' },
          apolloClient,
          true,
          locale,
        )
        if (foundAddress) newLocation = foundAddress as UserBasketSettingsLocationFragment
      }

      const minDateTime = getQueryDateTime(date, time)

      updateUserState({
        initalised: true,
        ...((newLocation || userState?.location) && {
          location: newLocation || userState?.location,
        }),
        ...(minDateTime && { date: minDateTime.valueOf() }),
        ...(minDateTime &&
          time && {
            time: `${format(minDateTime, 'HH:mm')} - ${format(addMinutes(minDateTime, 30), 'HH:mm')}`,
          }),
      })
    }

    void collectDetails()
  }, [
    pathname,
    postcode,
    street,
    date,
    time,
    apolloClient,
    locale,
    updateUserState,
    userState?.location,
  ])

  return {
    userState: { ...defaultUserState, ...userState },
    updateUserState,
    errors,
    setErrors: updateErrors,
  }
}
